import type {
  FindingType,
  TaskRunBriefEvidence,
  TaskRunBriefSectionEvidence,
} from "../../../../shared/types";

export type BriefEvidenceSection = {
  key: string;
  label: string;
  anchor: string;
  types: FindingType[];
};

export const BRIEF_EVIDENCE_SECTIONS: BriefEvidenceSection[] = [
  { key: "top_opportunities", label: "Top Opportunities", anchor: "top-opportunities", types: ["opportunity"] },
  { key: "threats_competitor_moves", label: "Threats / Competitor Moves", anchor: "threats-competitor-moves", types: ["threat"] },
  { key: "content_ideas", label: "Content Ideas", anchor: "content-ideas", types: ["content_idea"] },
  { key: "metrics", label: "Metrics", anchor: "metrics", types: ["metric"] },
  { key: "action_items", label: "Action Items", anchor: "action-items", types: ["action_item"] },
];

const EXPLICIT_ANCHOR_SUFFIX = /\s+\{#([a-z0-9-]+)\}\s*$/i;
const LEGACY_SOURCES_SUFFIX = /\s+\[Sources:\s*\d+\]\([^)]+\)\s*$/i;
const NEW_SOURCES_NOTE = /^\*Sources:\s*\d+;\s*see evidence below\.\*$/i;
const TRAILING_SOURCE_SUFFIX = /\s+\((Source:\s+.*|Internal source|Source unavailable(?:\s+\(.*\))?)\)\s*$/i;

function withoutLegacySourcesSuffix(value: string): string {
  return value.replace(LEGACY_SOURCES_SUFFIX, "").trim();
}

function stripExplicitAnchorSuffix(value: string): string {
  return withoutLegacySourcesSuffix(value).replace(EXPLICIT_ANCHOR_SUFFIX, "").trim();
}

function stripLegacySourcesSuffix(value: string): string {
  return value.replace(LEGACY_SOURCES_SUFFIX, "").trim();
}

export function extractExplicitAnchor(value: string): string | null {
  return sanitizeAnchor(withoutLegacySourcesSuffix(value).match(EXPLICIT_ANCHOR_SUFFIX)?.[1] ?? null);
}

export function sanitizeAnchor(value: string | null | undefined): string | null {
  if (!value) return null;
  const sanitized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return sanitized || null;
}

function explicitSectionEntry(
  section: BriefEvidenceSection,
  sectionEvidence: TaskRunBriefSectionEvidence[] | undefined,
): TaskRunBriefSectionEvidence | null {
  return sectionEvidence?.find((entry) => entry.section_key === section.key) ?? null;
}

export function resolveSectionAnchor(
  section: BriefEvidenceSection,
  sectionEvidence: TaskRunBriefSectionEvidence[] | undefined,
  contentLine?: string,
): string {
  const explicitContentAnchor = contentLine ? extractExplicitAnchor(contentLine) : null;
  return explicitContentAnchor || sanitizeAnchor(explicitSectionEntry(section, sectionEvidence)?.anchor) || section.anchor;
}

function inferSourceLabel(finding: TaskRunBriefEvidence): string | null {
  return finding.source_label ?? finding.domain ?? finding.query ?? null;
}

function sourceSummary(finding: TaskRunBriefEvidence): string | null {
  if (finding.source_access === "external" && finding.source_label) {
    return `Source: ${finding.source_label}`;
  }
  if (finding.source_access === "internal") {
    return "Internal source";
  }
  if (finding.source_access === "unavailable") {
    const label = inferSourceLabel(finding);
    return label ? `Source unavailable (${label})` : "Source unavailable";
  }
  const label = inferSourceLabel(finding);
  return label ? `Source: ${label}` : null;
}

function explicitSectionFindings(
  section: BriefEvidenceSection,
  sectionEvidence: TaskRunBriefSectionEvidence[] | undefined,
): TaskRunBriefEvidence[] | null {
  const explicit = explicitSectionEntry(section, sectionEvidence);
  return explicit ? explicit.findings : null;
}

export function evidenceCountForSection(
  section: BriefEvidenceSection,
  findings: TaskRunBriefEvidence[],
  sectionEvidence?: TaskRunBriefSectionEvidence[],
): number {
  const explicit = explicitSectionFindings(section, sectionEvidence);
  if (explicit) return explicit.length;
  return findings.filter((finding) => section.types.includes(finding.type)).length;
}

export function matchBriefEvidenceSection(line: string): BriefEvidenceSection | null {
  const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
  const boldSectionMatch = line.match(/^\*\*(.+?)\*\*(?:\s+\{#[a-z0-9-]+\})?(?:\s+\[Sources:\s*\d+\]\([^)]+\))?$/i);
  const sectionLabel = headingMatch
    ? stripLegacySourcesSuffix(stripExplicitAnchorSuffix(headingMatch[2]?.trim() ?? ""))
    : stripLegacySourcesSuffix(stripExplicitAnchorSuffix(boldSectionMatch?.[1]?.trim() ?? ""));

  if (!sectionLabel) return null;
  return BRIEF_EVIDENCE_SECTIONS.find((section) => section.label === sectionLabel) ?? null;
}

export function annotateBriefWithEvidence(
  content: string,
  findings: TaskRunBriefEvidence[],
  sectionEvidence?: TaskRunBriefSectionEvidence[],
): string {
  const lines = content.split("\n");
  let activeSection: BriefEvidenceSection | null = null;
  let activeSectionAnchor: string | null = null;
  const sectionIndices = new Map<string, number>();
  const sectionAnnotated = new Set<string>();

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? "";
    const section = matchBriefEvidenceSection(line);
    if (section) {
      activeSection = section;
      activeSectionAnchor = resolveSectionAnchor(activeSection, sectionEvidence, line);
      const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headingMatch) {
        const normalizedLabel = stripLegacySourcesSuffix(stripExplicitAnchorSuffix(headingMatch[2] ?? ""));
        lines[index] =
          activeSectionAnchor === activeSection.anchor
            ? `${headingMatch[1]} ${normalizedLabel}`
            : `${headingMatch[1]} ${normalizedLabel} {#${activeSectionAnchor}}`;
      } else {
        const boldSectionMatch = line.match(/^\*\*(.+?)\*\*(?:\s+\{#[a-z0-9-]+\})?(?:\s+\[Sources:\s*\d+\]\([^)]+\))?$/i);
        if (boldSectionMatch) {
          const normalizedLabel = stripLegacySourcesSuffix(stripExplicitAnchorSuffix(boldSectionMatch[1] ?? ""));
          lines[index] =
            activeSectionAnchor === activeSection.anchor
              ? `**${normalizedLabel}**`
              : `**${normalizedLabel}** {#${activeSectionAnchor}}`;
        }
      }
      const count = evidenceCountForSection(activeSection, findings, sectionEvidence);
      let updatedExistingMarker = false;
      for (let offset = 1; offset <= 4 && index + offset < lines.length; offset += 1) {
        const rawCandidate = lines[index + offset] ?? "";
        const candidate = rawCandidate.trim();
        if (/^(#{1,6}\s+|\*\*)/.test(candidate)) break;
        if (NEW_SOURCES_NOTE.test(candidate)) {
          if (count > 0) {
            lines[index + offset] = `*Sources: ${count}; see evidence below.*`;
            sectionIndices.set(activeSectionAnchor, sectionIndices.get(activeSectionAnchor) ?? 0);
          } else {
            lines.splice(index + offset, 1);
            if (lines[index + offset]?.trim() === "") {
              lines.splice(index + offset, 1);
            }
            if (lines[index + offset - 1]?.trim() === "") {
              lines.splice(index + offset - 1, 1);
            }
          }
          updatedExistingMarker = true;
          break;
        }
      }
      if (count <= 0) continue;
      if (updatedExistingMarker) continue;
      lines.splice(index + 1, 0, "", `*Sources: ${count}; see evidence below.*`, "");
      sectionIndices.set(activeSectionAnchor, sectionIndices.get(activeSectionAnchor) ?? 0);
      index += 3;
      continue;
    }

    if (!activeSection || !activeSectionAnchor) continue;
    const itemMatch = line.match(/^(\s*)(\d+\.|-|\*)\s+(.+)$/);
    const matchingFindings =
      explicitSectionFindings(activeSection, sectionEvidence)
      ?? findings.filter((finding) => activeSection!.types.includes(finding.type));
    const nextIndex = sectionIndices.get(activeSectionAnchor) ?? 0;
    const finding = matchingFindings[nextIndex];
    if (!finding) continue;

    const sourceText = sourceSummary(finding);
    if (itemMatch) {
      lines[index] = sourceText
        ? `${line.replace(TRAILING_SOURCE_SUFFIX, "")} (${sourceText})`
        : line.replace(TRAILING_SOURCE_SUFFIX, "");
      sectionIndices.set(activeSectionAnchor, nextIndex + 1);
      if (sourceText) sectionAnnotated.add(activeSectionAnchor);
      continue;
    }

    if (sectionAnnotated.has(activeSectionAnchor)) continue;
    if (!line.trim() || /^---/.test(line.trim()) || /^\*.+\*$/.test(line.trim())) continue;

    lines[index] = sourceText
      ? `${line.replace(TRAILING_SOURCE_SUFFIX, "")} (${sourceText})`
      : line.replace(TRAILING_SOURCE_SUFFIX, "");
    sectionIndices.set(activeSectionAnchor, nextIndex + 1);
    if (sourceText) sectionAnnotated.add(activeSectionAnchor);
  }

  return lines.join("\n");
}
